/* eslint-disable */
/**
 * transport.manager.js
 * Gerenciador Híbrido de Transporte em Tempo Real.
 * Suporta chaveamento entre Ably (Nuvem) e WebRTC (LAN Local).
 */

import { webrtcService } from './webrtc.provider';
import { nativeP2PService } from './native-p2p.provider';
import * as Ably from 'ably';
import { appEvents } from '@/store/app.events';
import appStore from '@/store/app.store';
import userStore from '@/store/user.store';

const STORAGE_MODE_KEY = 'triad_realtime_transport_mode';

class AblyRealtimeProvider {
    constructor() {
        this.client = null;
        this.channels = {};
    }

    getClient() {
        if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && window.location.port === '8080') {
            if (!this.mockClient) {
                this.mockClient = {
                    channels: {
                        get: () => ({
                            subscribe: () => {},
                            unsubscribe: () => {},
                            on: () => {},
                            off: () => {},
                            detach: () => {},
                            publish: () => Promise.resolve()
                        })
                    },
                    connection: {
                        on: () => {}
                    }
                };
            }
            return this.mockClient;
        }

        if (!this.client) {
            const clientId = window.myClientId || 'anonymous';
            this.client = new Ably.Realtime({ 
                authCallback: async (tokenParams, callback) => {
                    try {
                        const response = await window.fetch(`/api/ably/auth?clientId=${clientId}`);
                        if (!response) {
                            return callback(new Error('Ably auth intercepted'), null);
                        }
                        if (!response.ok) {
                            return callback(new Error('Ably auth failed: ' + response.status), null);
                        }
                        const tokenRequest = await response.json();
                        callback(null, tokenRequest);
                    } catch (error) {
                        callback(error, null);
                    }
                },
                echoMessages: false 
            });

            this.client.connection.on('connected', () => {
                console.log('[AblyRealtimeProvider] Conexão com Ably estabelecida/recuperada. Garantindo canais anexados...');
                Object.values(this.channels).forEach(channel => {
                    this.ensureAttached(channel);
                });
            });
        }
        return this.client;
    }

    ensureAttached(channel) {
        if (!channel) return;
        if (channel.state === 'attached' || channel.state === 'attaching') {
            return;
        }
        if (channel.state === 'detaching') {
            channel.once('detached', () => {
                channel.attach().catch((err) => {
                    console.warn('[AblyRealtimeProvider] Attach after detach failed:', err);
                });
            });
            return;
        }
        channel.attach().catch((err) => {
            console.warn('[AblyRealtimeProvider] Attach failed:', err);
        });
    }

    subscribe(channelName, eventName, onMessage, onStatusChange) {
        const client = this.getClient();
        const channel = client.channels.get(channelName);

        try { channel.off(); } catch (e) {}
        try { channel.unsubscribe(); } catch (e) {}
        this.channels[channelName] = channel;

        if (eventName === '*') {
            channel.subscribe((message) => {
                const data = message.data || {};
                data.event = message.name;
                onMessage(data);
            });
        } else {
            channel.subscribe(eventName, (message) => {
                const data = message.data || {};
                data.event = message.name;
                onMessage(data);
            });
        }

        this.ensureAttached(channel);

        if (onStatusChange) {
            channel.on((stateChange) => {
                let status = stateChange.current;
                if (status === 'attached') status = 'SUBSCRIBED';
                else if (status === 'failed') status = 'CHANNEL_ERROR';
                else if (status === 'suspended') status = 'TIMED_OUT';
                onStatusChange(status, stateChange.reason);
            });
            if (channel.state === 'attached') {
                onStatusChange('SUBSCRIBED', null);
            }
        }
        return channel;
    }

    /**
     * Inscrição crua: entrega (data, messageId) para permitir dedupe no TransportManager.
     * Mesma semântica do subscribe(), mas sem esconder o ID da mensagem do Ably.
     */
    subscribeRaw(channelName, eventName, onAblyMessage, onStatusChange) {
        const client = this.getClient();
        const channel = client.channels.get(channelName);

        try { channel.off(); } catch (e) {}
        try { channel.unsubscribe(); } catch (e) {}
        this.channels[channelName] = channel;

        const handler = (message) => {
            const data = message.data || {};
            const mid = data.__mid || message.id || null;
            onAblyMessage({ ...data, event: message.name, __mid: mid }, mid);
        };
        if (eventName === '*') {
            channel.subscribe(handler);
        } else {
            channel.subscribe(eventName, handler);
        }

        this.ensureAttached(channel);

        if (onStatusChange) {
            channel.on((stateChange) => {
                let status = stateChange.current;
                if (status === 'attached') status = 'SUBSCRIBED';
                else if (status === 'failed') status = 'CHANNEL_ERROR';
                else if (status === 'suspended') status = 'TIMED_OUT';
                onStatusChange(status, stateChange.reason);
            });
            if (channel.state === 'attached') {
                onStatusChange('SUBSCRIBED', null);
            }
        }
        return channel;
    }

    unsubscribe(channelName) {
        const client = this.getClient();
        const channel = client.channels.get(channelName);
        if (channel) {
            try { channel.off(); } catch (e) {}
            try { channel.unsubscribe(); } catch (e) {}
            // Não executa detach() síncrono para evitar race condition de detaching assíncrono
            // ao chavear de tela ou re-inscrever canais.
            delete this.channels[channelName];
        }
    }

    publish(channelName, eventName, data) {
        const client = this.getClient();
        const channel = client.channels.get(channelName);
        return channel.publish(eventName, data);
    }
}

class TransportManager {
    constructor() {
        this.ablyProvider = new AblyRealtimeProvider();
        this.webrtcProvider = webrtcService;
        this.nativeProvider = nativeP2PService;

        // Modo atual: 'cloud' (Ably) | 'local' (WebRTC) | 'direct' (P2P Nativo)
        // NOTA: o modo é apenas preferência/UI. A entrega é sempre REDUNDANTE (dual):
        // publica e escuta nos DOIS transportes com dedupe, para aparelhos em modos
        // diferentes (ou com modo stale no localStorage) continuarem se falando.
        this.mode = this.loadInitialMode();

        // Mapeamento de inscrições ativas para migrar dinamicamente se o modo mudar
        this.activeSubscriptions = new Map(); // key: channelName -> { eventName, onMessage, onStatusChange }

        // Dedupe de mensagens entregues pelos dois transportes: mid -> timestamp
        this.seenMessageIds = new Map();
        this.dedupeWindowMs = 2000;
    }

    rememberSeen(mid) {
        const now = Date.now();
        this.seenMessageIds.set(mid, now);
        if (this.seenMessageIds.size > 500) {
            for (const [key, ts] of this.seenMessageIds) {
                if (now - ts > this.dedupeWindowMs * 2) {
                    this.seenMessageIds.delete(key);
                } else {
                    break;
                }
            }
        }
    }

    fallbackKey(payload) {
        try {
            const { __mid, timestamp, ...rest } = payload || {};
            return `${payload?.event || ''}:${JSON.stringify(rest)}`;
        } catch (e) {
            return `${payload?.event || ''}:${Date.now()}`;
        }
    }

    dispatchDeduped(onMessage, payload, mid) {
        const key = mid || this.fallbackKey(payload);
        const now = Date.now();
        const last = this.seenMessageIds.get(key);
        if (last !== undefined && now - last < this.dedupeWindowMs) {
            return; // duplicata da entrega redundante (Ably + WebRTC + Native)
        }
        this.rememberSeen(key);
        // __mid é interno (dedupe/relay); não vaza para os handlers do app
        if (payload && typeof payload === 'object' && '__mid' in payload) {
            const { __mid, ...clean } = payload;
            payload = clean;
        }

        const isRealtimeEnabled = appStore?.state?.enabledRealtime ?? true;
        const eventName = payload?.event || payload?.name;
        const isControlDelegation = eventName === 'realtime-user-changed' || payload?.realtime_user_changed || (payload?.data && payload?.data?.realtime_user_changed);
        if (!isRealtimeEnabled && !isControlDelegation) {
            console.log(`[TransportManager] incoming dispatch BLOCKED (realtime disabled) for event=${eventName}`);
            return;
        }

        try {
            onMessage(payload);
        } catch (err) {
            console.error('[TransportManager] Erro no handler do canal:', err);
        }
    }

    isUserFree() {
        try {
            return !!userStore?.getters?.['isFree'];
        } catch (e) {
            return false;
        }
    }

    loadInitialMode() {
        if (this.isUserFree()) {
            return 'cloud';
        }
        if (typeof localStorage !== 'undefined') {
            const saved = localStorage.getItem(STORAGE_MODE_KEY);
            if (saved === 'local' || saved === 'cloud' || saved === 'direct') {
                return saved;
            }
        }
        return 'cloud'; // Padrão
    }

    setMode(newMode) {
        if (newMode !== 'cloud' && newMode !== 'local' && newMode !== 'direct') return;

        // Usuários Free são restritos exclusivamente ao modo Cloud (Ably)
        if (newMode !== 'cloud' && this.isUserFree()) {
            console.warn(`[TransportManager] Modo ${newMode} bloqueado para usuário Free. Mantendo Cloud.`);
            return;
        }

        if (this.mode === newMode) return;

        console.log(`[TransportManager] Chaveando modo de transporte: ${this.mode} -> ${newMode}`);
        this.mode = newMode;
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(STORAGE_MODE_KEY, newMode);
        }

        // Entrega dual: reinscreve todos os canais ativos nos DOIS provedores
        // (o modo é preferência/UI; o roteamento não depende mais dele).
        this.activeSubscriptions.forEach((sub, channelName) => {
            try {
                this.subscribe(channelName, sub.eventName, sub.onMessage, sub.onStatusChange);
            } catch (e) {
                console.warn('[TransportManager] Erro ao re-inscrever canal:', e);
            }
        });

        appEvents.emit('realtime:mode-changed', newMode);
    }

    getMode() {
        if (this.isUserFree() && this.mode !== 'cloud') {
            this.mode = 'cloud';
        }
        return this.mode;
    }

    getActiveProvider() {
        return this.mode === 'local' ? this.webrtcProvider : this.ablyProvider;
    }

    // --- Interface Unificada (Compatibilidade com realtimeService) ---
    // Entrega DUAL: escuta e publica em AMBOS os transportes. Sem isso, aparelhos em
    // modos diferentes (ex: um ficou com 'local' persistido do teste na LAN e outro em
    // 'cloud') nunca se encontram — o change-song via Ably não chega a ninguém.

    subscribe(channelName, eventName, onMessage, onStatusChange) {
        this.activeSubscriptions.set(channelName, { eventName, onMessage, onStatusChange });

        // Limpa inscrições anteriores para não acumular handlers duplicados (ex: troca de música).
        try { this.ablyProvider.unsubscribe(channelName); } catch (e) {}
        try { this.webrtcProvider.unsubscribe(channelName); } catch (e) {}
        try { this.nativeProvider.unsubscribe(channelName); } catch (e) {}

        const forward = (payload, mid) => {
            const effectiveMid = payload?.__mid || mid;
            this.dispatchDeduped(onMessage, payload, effectiveMid);
        };

        // Status combinado: SUBSCRIBED se QUALQUER transporte estiver conectado.
        const statusState = { ably: null, webrtc: null, native: null };
        const emitCombinedStatus = () => {
            if (typeof onStatusChange !== 'function') return;
            const up = statusState.ably === 'SUBSCRIBED' || statusState.webrtc === 'SUBSCRIBED' || statusState.native === 'SUBSCRIBED';
            try {
                onStatusChange(
                    up ? 'SUBSCRIBED' : (statusState.ably || statusState.webrtc || statusState.native || 'DISCONNECTED'),
                    null
                );
            } catch (e) {}
        };

        try {
            this.ablyProvider.subscribeRaw(
                channelName,
                eventName,
                (payload, mid) => forward(payload, payload?.__mid || mid),
                (status) => { statusState.ably = status; emitCombinedStatus(); }
            );
        } catch (e) {
            console.warn('[TransportManager] Falha ao inscrever canal no Ably:', e);
        }

        try {
            this.webrtcProvider.subscribe(
                channelName,
                eventName,
                (payload) => forward(payload, payload?.__mid),
                (status) => { statusState.webrtc = status; emitCombinedStatus(); }
            );
        } catch (e) {
            console.warn('[TransportManager] Falha ao inscrever canal no WebRTC:', e);
        }

        try {
            this.nativeProvider.subscribe(
                channelName,
                eventName,
                (payload) => forward(payload, payload?.__mid),
                (status) => { statusState.native = status; emitCombinedStatus(); }
            );
        } catch (e) {
            console.warn('[TransportManager] Falha ao inscrever canal no NativeP2P:', e);
        }

        // Fachada compatível com o channel do Ably (ex: SongCanvas usa .publish()).
        const manager = this;
        return {
            publish(eventNameArg, dataArg) {
                return manager.publish(channelName, eventNameArg, dataArg);
            },
            unsubscribe() {
                return manager.unsubscribe(channelName);
            }
        };
    }

    unsubscribe(channelName) {
        this.activeSubscriptions.delete(channelName);
        try { this.ablyProvider.unsubscribe(channelName); } catch (e) {}
        try { this.webrtcProvider.unsubscribe(channelName); } catch (e) {}
        try { this.nativeProvider.unsubscribe(channelName); } catch (e) {}
    }

    hasWebRTCPeers() {
        try {
            let found = false;
            this.webrtcProvider.connections.forEach(peer => {
                if (peer.dc && peer.dc.readyState === 'open') found = true;
            });
            return found;
        } catch (e) {
            return false;
        }
    }

    hasNativePeers() {
        try {
            return (this.nativeProvider.getConnectedCount() || 0) > 0;
        } catch (e) {
            return false;
        }
    }

    publish(channelName, eventName, data) {
        const isRealtimeEnabled = appStore?.state?.enabledRealtime ?? true;
        const isControlDelegation = eventName === 'realtime-user-changed' || (data && data.realtime_user_changed);
        if (!isRealtimeEnabled && !isControlDelegation) {
            console.log(`[TransportManager] publish BLOCKED (realtime disabled) for event=${eventName} channel=${channelName}`);
            return Promise.resolve([{ transport: 'all', ok: true, skipped: true, reason: 'realtime-disabled' }]);
        }

        // Gera um ID universal compartilhado para permitir dedupe perfeito em todos os transportes
        const mid = (data && typeof data === 'object' && data.__mid)
            ? data.__mid
            : `tm_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        const payloadData = (data && typeof data === 'object')
            ? { ...data, __mid: mid }
            : data;

        // Best-effort nos transportes disponíveis, com timeout: nunca rejeita nem pendura.
        const withTimeout = (promise, ms, label) => Promise.race([
            Promise.resolve(promise).then(
                (value) => ({ transport: label, ok: true, value }),
                (error) => ({ transport: label, ok: false, error: String((error && error.message) || error) })
            ),
            new Promise((resolve) => setTimeout(() => resolve({ transport: label, ok: false, timeout: true }), ms))
        ]);

        const isFree = this.isUserFree();
        const jobs = [];

        // WebRTC: envia se houver conexões locais abertas
        try {
            jobs.push(
                this.hasWebRTCPeers()
                    ? withTimeout(this.webrtcProvider.publish(channelName, eventName, payloadData), 2000, 'webrtc')
                    : Promise.resolve({ transport: 'webrtc', ok: true, skipped: true, reason: 'no-peers' })
            );
        } catch (e) {
            jobs.push(Promise.resolve({ transport: 'webrtc', ok: false, error: String(e) }));
        }

        // Native P2P (BLE / Hotspot): envia se houver peers conectados
        try {
            jobs.push(
                this.hasNativePeers()
                    ? withTimeout(this.nativeProvider.publish(channelName, eventName, payloadData), 2000, 'native')
                    : Promise.resolve({ transport: 'native', ok: true, skipped: true, reason: 'no-peers' })
            );
        } catch (e) {
            jobs.push(Promise.resolve({ transport: 'native', ok: false, error: String(e) }));
        }

        // Ably: pula se comprovadamente offline (evita retry/ruído em palco sem internet).
        // Se houver peers locais ativos (WebRTC ou BLE), reduz timeout para 1200ms para evitar retenção.
        try {
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                jobs.push(Promise.resolve({ transport: 'ably', ok: true, skipped: true, reason: 'offline' }));
            } else {
                const ablyTimeout = (this.hasWebRTCPeers() || this.hasNativePeers()) ? 1200 : 2500;
                jobs.push(withTimeout(this.ablyProvider.publish(channelName, eventName, payloadData), ablyTimeout, 'ably'));
            }
        } catch (e) {
            jobs.push(Promise.resolve({ transport: 'ably', ok: false, error: String(e) }));
        }

        return Promise.all(jobs);
    }

    // Helper direto para WebRTC P2P
    getWebRTC() {
        return this.webrtcProvider;
    }
}

export const transportManager = new TransportManager();
export default transportManager;
